import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useCreateUser } from '../Providers/CreateUserProvider/CreateUserContext';
import { getRoles } from '../../services/roleService';

const shifts = ['Morning', 'Evening', 'Night'];

const validationSchema = Yup.object({
  role: Yup.string().required('Role is required'),
  shift: Yup.string().required('Shift is required'),
});

const RoleSelectionForm: React.FC = () => {
  const { createUserData, setCreateUserData } = useCreateUser();

  const [roles, setRoles] = useState<{ id: number; title: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      role: createUserData.role || '',
      shift: createUserData.shift || '',
    },
    validationSchema,
    onSubmit: () => {},
  });

  useEffect(() => {
    const fetchRoles = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getRoles();
        setRoles(data || []);
      } catch (err) {
        console.error('Failed to load roles:', err);
        setError('Failed to load roles');
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // Custom onChange for role to sync both role ID and role title to context
  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roleId = e.target.value;
    formik.handleChange(e);
    const roleTitle = roles.find((r) => r.id.toString() === roleId)?.title || '';
    setCreateUserData({
      ...createUserData,
      role: roleId,
      roleName: roleTitle,
    });
  };

  // Sync shift unchanged as usual
  useEffect(() => {
    setCreateUserData((prev) => ({
      ...prev,
      shift: formik.values.shift,
    }));
  }, [formik.values.shift, setCreateUserData]);

  return (
    <div className="flex flex-wrap gap-8 items-start p-6">
      {/* Role Dropdown */}
      <div className="flex flex-col w-full md:w-[45%]">
        <label htmlFor="role" className="text-sm font-semibold text-slate-900 mb-2">
          Role
        </label>
        <div className="relative">
          {loading ? (
            <p className="text-gray-500 text-sm">Loading roles...</p>
          ) : error ? (
            <p className="text-red-500 text-sm">{error}</p>
          ) : (
            <select
              id="role"
              name="role"
              value={formik.values.role}
              onChange={handleRoleChange}
              onBlur={formik.handleBlur}
              className={`appearance-none bg-slate-50 border ${
                formik.touched.role && formik.errors.role ? 'border-red-500' : 'border-gray-300'
              } rounded-md px-4 py-2 text-gray-600 focus:outline-none focus:outline-none focus:ring-0 focus:border-gray-300 w-full`}
            >
              <option value="">Select Role</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.title}
                </option>
              ))}
            </select>
          )}
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {formik.touched.role && formik.errors.role && <p className="text-red-500 text-sm mt-1">{formik.errors.role}</p>}
      </div>

      {/* Shift Dropdown */}
      <div className="flex flex-col w-full md:w-[45%]">
        <label htmlFor="shift" className="text-sm font-semibold text-slate-900 mb-2">
          Shift
        </label>
        <div className="relative">
          <select
            id="shift"
            name="shift"
            value={formik.values.shift}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`appearance-none bg-slate-50 border ${
              formik.touched.shift && formik.errors.shift ? 'border-red-500' : 'border-gray-300'
            } rounded-md px-4 py-2 text-gray-600 focus:outline-none focus:outline-none focus:ring-0 focus:border-gray-300 w-full`}
          >
            <option value="">Select Shift</option>
            {shifts.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {formik.touched.shift && formik.errors.shift && <p className="text-red-500 text-sm mt-1">{formik.errors.shift}</p>}
      </div>
    </div>
  );
};

export default RoleSelectionForm;
